// carouselLogic.js — PURE, framework-free core for the Image Carousel content
// block (DL-041, "film-strip" LOCKED deck spec). Every branch that decides WHAT
// the carousel does (which slide is next, whether auto-play may run, which slides
// are eagerly loaded, how a swipe/keypress maps to an index, how a Fit mode maps
// to object-fit) lives here so it is unit-tested without a DOM. The React view
// (CarouselView.jsx) only wires refs/measurement to these decisions.
//
// Tokens/behaviour are the deck's: manual arrows/dots/swipe by default; auto-play
// pauses off-screen and is suppressed under reduced-motion; only the current +
// its immediate neighbours are loaded (never the whole strip at once).

// Auto-advance dwell per Speed choice (ms). Manual mode never uses these.
export const SPEED_MS = { slow: 6000, medium: 4000, fast: 2500 };

export const speedToMs = (speed) =>
  Object.prototype.hasOwnProperty.call(SPEED_MS, speed)
    ? SPEED_MS[speed]
    : SPEED_MS.medium;

// Clamp an index into [0, count-1]; empty strip => 0.
export const clampIndex = (i, count) => {
  const n = Number(count) || 0;
  if (n < 1) return 0;
  const idx = Number.isFinite(i) ? Math.trunc(i) : 0;
  return Math.max(0, Math.min(idx, n - 1));
};

export const nextIndex = (current, count, loop = true) => {
  const n = Number(count) || 0;
  if (n <= 1) return 0;
  const c = clampIndex(current, n);
  if (c >= n - 1) return loop ? 0 : n - 1;
  return c + 1;
};

export const prevIndex = (current, count, loop = true) => {
  const n = Number(count) || 0;
  if (n <= 1) return 0;
  const c = clampIndex(current, n);
  if (c <= 0) return loop ? n - 1 : 0;
  return c - 1;
};

// T22.163 (Kelly, 2026-10-12): stepping PAST an end of a looping strip must not
// slide the track back across every image. Returns which edge clone the view should
// animate to instead: "tail" (a copy of the first image parked after the last) when
// Next wraps last -> first, "head" (a copy of the last image parked before the
// first) when Prev wraps first -> last, else null (an ordinary one-slide step).
export const loopWrapEdge = (current, target, count, dir) => {
  const n = Number(count) || 0;
  if (n <= 1) return null;
  if (dir > 0 && current === n - 1 && target === 0) return "tail";
  if (dir < 0 && current === 0 && target === n - 1) return "head";
  return null;
};

// Deck: "if auto-play is on it pauses when the block scrolls off-screen; and it
// honours reduced-motion". Auto-advance is allowed ONLY when the author chose
// Auto, the strip has >1 slide, it is on-screen, reduced-motion is not requested,
// and we are not in the builder (authoring should never animate on its own).
export const shouldAutoAdvance = ({
  mode,
  reducedMotion,
  inView,
  count,
  editing = false,
} = {}) =>
  mode === "auto" &&
  !reducedMotion &&
  !!inView &&
  !editing &&
  (Number(count) || 0) > 1;

// Deck: "Only the current and next image are loaded (never all of them at once)".
// We eagerly load the current slide + its immediate next/prev neighbours (prev so
// a back-swipe peek is never blank); everything else stays unfetched until it
// becomes a neighbour. Returns true if slide `index` should carry a real src.
export const isSlideLoaded = (index, current, count, loop = true) => {
  const n = Number(count) || 0;
  if (n <= 0) return false;
  const c = clampIndex(current, n);
  if (index === c) return true;
  if (index === nextIndex(c, n, loop)) return true;
  if (index === prevIndex(c, n, loop)) return true;
  return false;
};

// Fit modes (deck "How it handles mixed shapes"): the film-strip default keeps
// each image's own shape at a shared height (no crop, no letterbox bars) — that
// is object-fit: contain on a natural-width cell. Crop = cover (equalised uniform
// cell); Fill = fill (stretch to the cell). Keep-Shape is the LOCKED default.
export const FIT_MODES = { keepShape: "keep-shape", crop: "crop", fill: "fill" };

export const fitToObjectFit = (fit) => {
  switch (fit) {
    case FIT_MODES.crop:
      return "cover";
    case FIT_MODES.fill:
      return "fill";
    case FIT_MODES.keepShape:
    default:
      return "contain";
  }
};

// Keep-Shape lets each cell size to the image's own ratio (width:auto at a shared
// height); Crop/Fill equalise every cell to one uniform width so the row is a
// tidy grid. Consumers use this to decide the cell's width behaviour.
export const isUniformCell = (fit) => fit === FIT_MODES.crop || fit === FIT_MODES.fill;

// Swipe/drag: given the horizontal delta (px, negative = dragged left = go next)
// and the slide width, decide the resulting index. Threshold is 25% of the slide
// (min 40px) so a small nudge snaps back to the current slide.
export const swipeToIndex = (deltaX, slideWidth, current, count, loop = true) => {
  const n = Number(count) || 0;
  const threshold = Math.max(40, (Number(slideWidth) || 0) * 0.25);
  if (deltaX <= -threshold) return nextIndex(current, n, loop);
  if (deltaX >= threshold) return prevIndex(current, n, loop);
  return clampIndex(current, n);
};

// Keyboard (WAI-ARIA carousel): Arrow keys step, Home/End jump. Returns the new
// index, or null when the key is not a carousel control (so the caller can leave
// the event alone / not preventDefault).
export const keyToIndex = (key, current, count, loop = true) => {
  const n = Number(count) || 0;
  switch (key) {
    case "ArrowRight":
      return nextIndex(current, n, loop);
    case "ArrowLeft":
      return prevIndex(current, n, loop);
    case "Home":
      return 0;
    case "End":
      return n > 0 ? n - 1 : 0;
    default:
      return null;
  }
};

// Max number of images the film-strip will ever render. The view renders ONE
// <Slide> wrapper + ONE <Dot> per image and `recenter()` reflows the whole track
// on every layout/resize, so an uncapped list (a crafted/pathological saved wheel
// of 100/300 images) means linear DOM growth + O(n) reflow. Cap the normalised list
// so both are bounded by construction. 60 is generous vs. any realistic film-strip
// (real use ~5-20) yet keeps the pathological case cheap (~420 DOM nodes at the cap
// vs. ~2100+ at 300). This is a VIEW-time cap only (normalizeImages runs at render,
// never on the save path) so no stored data is lost. Exported => single tunable knob.
export const MAX_CAROUSEL_IMAGES = 60;

// Max caption length (chars). A slide's caption is its image `title`, which
// deserializes from the saved-wheel API and (like the block's other props) is
// NOT length-bounded there. The CaptionPill CSS truncates the DISPLAY to one
// ellipsised line, but the full string still lands in the DOM as a text node, so
// a crafted/pathological multi-KB title (× up to MAX_CAROUSEL_IMAGES) is a real
// DOM-bloat vector. Cap the caption at normalise-time — the SAME view-time
// chokepoint that bounds the item count — matching the app's caption/title cap
// convention (TableEditor CAPTION_MAX / Form+TitleBar TITLE_MAX = 120). CSS still
// owns the visible ellipsis; this only bounds the underlying text node. View-time
// only (never the save path) => no stored data lost. `<= 0` disables the cap.
export const MAX_CAROUSEL_CAPTION_LEN = 120;

// Normalise the stored images prop into a stable [{ id, src, title }] list.
// Accepts objects or bare url strings; drops entries with no usable src; assigns
// a deterministic fallback id so React keys + reorder stay stable; caps the
// result at `max` (default MAX_CAROUSEL_IMAGES) — early-exiting so a pathological
// list never costs more than the cap (`max <= 0` disables it) — and caps each
// caption at `captionMax` chars (default MAX_CAROUSEL_CAPTION_LEN; `<= 0` off).
export const normalizeImages = (
  images,
  max = MAX_CAROUSEL_IMAGES,
  captionMax = MAX_CAROUSEL_CAPTION_LEN
) => {
  const arr = Array.isArray(images) ? images : [];
  const cap = Number.isFinite(max) && max > 0 ? Math.trunc(max) : Infinity;
  const capLen =
    Number.isFinite(captionMax) && captionMax > 0
      ? Math.trunc(captionMax)
      : Infinity;
  const out = [];
  for (let i = 0; i < arr.length && out.length < cap; i += 1) {
    const im = arr[i];
    if (!im) continue;
    const o = typeof im === "string" ? { src: im } : im;
    if (!o.src) continue;
    const title = typeof o.title === "string" ? o.title.slice(0, capLen) : "";
    out.push({
      id: o.id != null ? o.id : `carousel-${i}`,
      src: o.src,
      title,
    });
  }
  return out;
};

const carouselLogic = {
  SPEED_MS,
  speedToMs,
  clampIndex,
  nextIndex,
  prevIndex,
  loopWrapEdge,
  shouldAutoAdvance,
  isSlideLoaded,
  FIT_MODES,
  fitToObjectFit,
  isUniformCell,
  swipeToIndex,
  keyToIndex,
  normalizeImages,
  MAX_CAROUSEL_IMAGES,
  MAX_CAROUSEL_CAPTION_LEN,
};

export default carouselLogic;
